// Debounced patient lookup for the calendar's entry sheet: type ≥2 characters, pick a
// result, and the field collapses into a selected-patient card with Change / Clear.
// Extracted from SlotBookingModal's search (calendar-event-item.jsx), minus the
// new-patient quick-create — a direct calendar entry either links an existing patient
// record or none at all.

import { useEffect, useRef, useState } from "react";
import { Search, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { searchPatients } from "@/lib/api/users";

const SEARCH_DEBOUNCE_MS = 400;
const MIN_QUERY_LENGTH = 2;

/** "First Last" → displayName → id, mirroring the slot-booking modal's label. */
export function patientDisplayName(patient) {
  if (!patient || typeof patient !== "object") return "";
  const displayName = typeof patient.displayName === "string" ? patient.displayName.trim() : "";
  if (displayName) return displayName;
  return `${patient.firstName || ""} ${patient.lastName || ""}`.trim() || String(patient.id || "").trim();
}

function formatDob(value) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value || "") : date.toLocaleDateString();
}

/**
 * `value` is the selected patient object (a `/api/users/patients:search` item) or null;
 * `onChange(patient | null)`.
 */
export function PatientSearchField({ value = null, onChange, disabled = false, className }) {
  const [term, setTerm] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (value) return undefined;
    const query = term.trim();
    if (query.length < MIN_QUERY_LENGTH) {
      setResults([]);
      setSearching(false);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const response = await searchPatients({ searchTerm: query });
        if (!cancelled) setResults(Array.isArray(response?.items) ? response.items : []);
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to search patients:", error);
          setResults([]);
        }
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [term, value]);

  const select = (patient) => {
    setResults([]);
    setTerm("");
    onChange?.(patient);
  };

  if (value) {
    const name = patientDisplayName(value) || "Selected patient";
    const dob = value?.profile?.DOB || value?.dob;
    const sex = value?.profile?.Sex || value?.sex;
    return (
      <div
        className={cn(
          "flex flex-col gap-1 rounded-md border border-emerald-500/20 bg-emerald-500/10 p-3 text-emerald-700 dark:text-emerald-400",
          className,
        )}
      >
        <div className="flex items-center justify-between gap-2">
          <span className="flex min-w-0 items-center gap-2 text-sm font-semibold">
            <User className="size-4 shrink-0" aria-hidden />
            <span className="truncate">{name}</span>
          </span>
          <span className="flex shrink-0 items-center gap-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={disabled}
              className="h-6 text-xs text-emerald-700 hover:bg-emerald-500/20 hover:text-emerald-800 dark:text-emerald-400 dark:hover:text-emerald-300"
              onClick={() => {
                onChange?.(null);
                // Back to the search box, ready for the next name.
                window.requestAnimationFrame?.(() => inputRef.current?.focus());
              }}
            >
              Change
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={disabled}
              className="h-6 text-xs text-muted-foreground hover:bg-emerald-500/20 hover:text-foreground"
              onClick={() => onChange?.(null)}
            >
              Clear
            </Button>
          </span>
        </div>
        {value.email || value.phone || dob || sex ? (
          <div className="mt-1 grid grid-cols-2 gap-x-2 gap-y-1 text-xs opacity-90">
            {value.email ? (
              <div className="truncate">
                <span className="font-medium opacity-70">Email:</span> {value.email}
              </div>
            ) : null}
            {value.phone ? (
              <div className="truncate">
                <span className="font-medium opacity-70">Phone:</span> {value.phone}
              </div>
            ) : null}
            {dob ? (
              <div className="truncate">
                <span className="font-medium opacity-70">DOB:</span> {formatDob(dob)}
              </div>
            ) : null}
            {sex ? (
              <div className="truncate">
                <span className="font-medium opacity-70">Sex:</span> {sex}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  }

  const query = term.trim();
  return (
    <div className={cn("space-y-2", className)}>
      <div className="relative">
        <Search className="pointer-events-none absolute left-2.5 top-2.5 size-4 text-muted-foreground" aria-hidden />
        <Input
          ref={inputRef}
          placeholder="Search patient by name…"
          className="pl-9"
          value={term}
          disabled={disabled}
          onChange={(event) => setTerm(event.target.value)}
          aria-label="Search patients"
        />
        {searching ? (
          <span
            className="absolute right-2.5 top-2.5 block size-4 animate-spin rounded-full border-2 border-primary border-t-transparent"
            aria-hidden
          />
        ) : null}
      </div>
      {results.length > 0 ? (
        <ScrollArea viewportClassName="max-h-40" className="rounded-md border bg-muted/30">
          <div className="space-y-1 p-1.5">
            {results.map((patient) => (
              <button
                key={patient.id}
                type="button"
                disabled={disabled}
                onClick={() => select(patient)}
                className="w-full rounded-md px-3 py-2 text-left text-sm font-medium transition-colors hover:bg-muted"
              >
                <span className="block truncate">{patientDisplayName(patient) || patient.id}</span>
                {patient.phone ? (
                  <span className="block truncate text-xs font-normal text-muted-foreground">{patient.phone}</span>
                ) : null}
              </button>
            ))}
          </div>
        </ScrollArea>
      ) : null}
      {query.length >= MIN_QUERY_LENGTH && !searching && results.length === 0 ? (
        <p className="text-xs text-muted-foreground">No patients found.</p>
      ) : null}
    </div>
  );
}
